import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, ButtonGroup, Card, Form, Table } from "react-bootstrap";
import { FaArchive, FaClone, FaDownload, FaEdit, FaPlus, FaSave, FaTimes, FaUndo, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import SettingsWorkbenchLayout, {
  SettingsWorkbenchItem,
} from "@shared/components/SettingsWorkbenchLayout";
import {
  Channel, ElectrodeSystem,
} from "../../Montage/types/montage.types";
import {
  createChannel,
  createElectrodeSystem,
  cloneElectrodeSystem,
  archiveElectrodeSystem,
  fetchElectrodeSystems,
  fetchElectrodeSystemAccess,
  fetchChannels,
  updateChannel as saveChannel,
} from "../../Montage/services/MontageService";
import { useAuth } from "@shared/context/AuthContext";
import {
  detectInterchangeFormat,
  downloadInterchangeFile,
  InterchangeFormat,
  parseChannelsInterchange,
  serializeChannels,
  validateChannelsInterchange,
} from "../../EEG/utils/eegInterchange";
import { ChannelCatalogMode } from "../types/eegConfig.types";
import EegConfigPageShell from "../components/EegConfigPageShell";
import { getPasswordManagerIgnoreFormProps } from "@shared/utils/nonAuthPasswordFields";
import "../styles/eegConfig.css";

interface ChannelCatalogPageProps {
  mode: ChannelCatalogMode;
}

const MODE_COPY: Record<
  ChannelCatalogMode,
  { title: string; description: string; listTitle: string; addLabel: string; fallbackGroup: string }
> = {
  electrodes: {
    title: "Electrode Systems",
    description:
      "Manage electrode systems, display metadata, coordinates, and default colors.",
    listTitle: "Systems",
    addLabel: "Add electrode",
    fallbackGroup: "10-20 Custom",
  },
  references: {
    title: "EEG References",
    description:
      "Manage reference electrodes and combinations used by referential montages.",
    listTitle: "Reference Sets",
    addLabel: "Custom EEG Reference",
    fallbackGroup: "References",
  },
  "non-eeg": {
    title: "Non-EEG Signals",
    description:
      "Manage auxiliary channels such as EOG, ECG, EMG, respiration, and body position.",
    listTitle: "Signal Groups",
    addLabel: "Custom Non-EEG Signal",
    fallbackGroup: "Auxiliary",
  },
};

const isReferenceChannel = (channel: Channel) =>
  channel.region?.toLowerCase() === "reference" ||
  channel.electrode_system?.toLowerCase() === "reference" ||
  ["A1", "A2", "M1", "M2", "CZ", "REF", "AVG"].includes(
    channel.channel_code.toUpperCase(),
  );

const isNonEegChannel = (channel: Channel) =>
  ["auxiliary", "special", "virtual"].includes(
    channel.electrode_system?.toLowerCase(),
  ) ||
  /^(EOG|ECG|EKG|EMG|RESP|BODY|DC|PG)/i.test(channel.channel_code);

const filterChannelsByMode = (channels: Channel[], mode: ChannelCatalogMode) => {
  if (mode === "references") {
    return channels.filter(isReferenceChannel);
  }
  if (mode === "non-eeg") {
    return channels.filter(isNonEegChannel);
  }
  return channels.filter(
    (channel) => !isReferenceChannel(channel) && !isNonEegChannel(channel),
  );
};

const groupKeyForMode = (channel: Channel, mode: ChannelCatalogMode) => {
  if (mode === "references") return channel.electrode_system || "References";
  if (mode === "non-eeg") return channel.region || channel.electrode_system;
  return channel.electrode_system || "Unassigned";
};

/** Display label for catalog group keys (DB values are often lowercase). */
const formatGroupLabel = (group: string): string => {
  const trimmed = String(group || "").trim();
  if (!trimmed) return trimmed;
  return trimmed
    .split(/([\s/_-]+)/)
    .map((part) =>
      /^[\s/_-]+$/.test(part)
        ? part
        : part.charAt(0).toLocaleUpperCase() + part.slice(1),
    )
    .join("");
};

const createDraftChannel = (
  mode: ChannelCatalogMode,
  group: string,
): Channel => ({
  channel_id: `draft-${Date.now()}`,
  channel_code:
    mode === "non-eeg" ? "AUX" : mode === "references" ? "REF" : "New",
  channel_name:
    mode === "non-eeg"
      ? "Auxiliary Signal"
      : mode === "references"
        ? "Reference Electrode"
        : "Custom Electrode",
  electrode_system:
    mode === "references"
      ? group || "reference"
      : mode === "non-eeg"
        ? "auxiliary"
        : group,
  hemisphere: "midline",
  region:
    mode === "references"
      ? "reference"
      : mode === "non-eeg"
        ? group || "auxiliary"
        : group || "custom",
  display_order: 999,
  default_color: "#0d6efd",
  is_standard: false,
  age_group: "all",
  is_active: true,
  coordinate_source: "custom_normalized",
  coordinate_space: "nervedx_normalized_scalp",
  coordinate_units: "normalized",
});

const adaptImportedChannelsForMode = (
  channels: Channel[],
  mode: ChannelCatalogMode,
): Channel[] =>
  channels.map((channel, index) => {
    const adapted: Channel = {
      ...channel,
      channel_id: `import-${Date.now()}-${index}`,
      coordinate_source: "imported",
      coordinate_space: "nervedx_normalized_scalp",
      coordinate_units: "normalized",
    };
    if (mode === "references") {
      return {
        ...adapted,
        region: "reference",
        electrode_system:
          channel.electrode_system?.toLowerCase() === "reference"
            ? channel.electrode_system
            : channel.electrode_system || "References",
      };
    }
    if (mode === "non-eeg") {
      return {
        ...adapted,
        electrode_system: channel.electrode_system || "auxiliary",
        region: channel.region || "auxiliary",
      };
    }
    return adapted;
  });

const ChannelCatalogPage: React.FC<ChannelCatalogPageProps> = ({ mode }) => {
  const { t } = useTranslation("features");
  const { user } = useAuth();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [systems, setSystems] = useState<ElectrodeSystem[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [rowSnapshots, setRowSnapshots] = useState<Map<string, Channel>>(new Map());
  const [selectedGroup, setSelectedGroup] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState<InterchangeFormat>("tsv");
  const [editingPositions, setEditingPositions] = useState<Set<string>>(new Set());
  const importInputRef = useRef<HTMLInputElement>(null);
  const passwordManagerIgnore = getPasswordManagerIgnoreFormProps();
  const copy = MODE_COPY[mode];
  const localizedCopy = {
    title: t(`ui.eegConfig.channelCatalog.${mode}.title`, {
      defaultValue: copy.title,
    }),
    description: t(
      `ui.eegConfig.channelCatalog.${mode}.description`,
      { defaultValue: copy.description },
    ),
    listTitle: t(
      `ui.eegConfig.channelCatalog.${mode}.listTitle`,
      { defaultValue: copy.listTitle },
    ),
    addLabel: t(`ui.eegConfig.channelCatalog.${mode}.addLabel`, {
      defaultValue: copy.addLabel,
    }),
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [rows, systemRows] = await Promise.all([fetchChannels(), mode === "electrodes" ? fetchElectrodeSystems() : Promise.resolve([])]);
        if (cancelled) return;
        const filtered = filterChannelsByMode(rows, mode);
        setChannels(filtered);
        setSystems(systemRows);
        setSelectedGroup(mode === "electrodes" ? systemRows[0]?.system_id || "" : filtered[0] ? groupKeyForMode(filtered[0], mode) : "");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  useEffect(() => {
    let active = true;
    void fetchElectrodeSystemAccess().then((access) => { if (active) { setCanManage(access.can_manage_org_eeg_config); setIsPlatformAdmin(access.is_platform_admin); } });
    return () => { active = false; };
  }, [user?.role]);

  const groups: SettingsWorkbenchItem[] = useMemo(() => {
    if (mode === "electrodes") return systems
      .filter((system) => system.system_name.toLowerCase().includes(search.toLowerCase()))
      .map((system) => ({ id: system.system_id, title: system.system_name, subtitle: `${system._count?.electrodes ?? channels.filter((row) => row.system_id === system.system_id).length} electrodes`, badge: system.is_standard ? "Standard" : "Custom" }));
    const byGroup = new Map<string, number>();
    channels.forEach((channel) => {
      const group = groupKeyForMode(channel, mode);
      byGroup.set(group, (byGroup.get(group) || 0) + 1);
    });
    return Array.from(byGroup.entries())
      .filter(([group]) => group.toLowerCase().includes(search.toLowerCase()))
      .map(([group, count]) => ({
        id: group,
        title: formatGroupLabel(group),
        subtitle: t("ui.eegConfig.channelCatalog.channelCount", {
          count,
          defaultValue: count === 1 ? "{{count}} channel" : "{{count}} channels",
        }),
        badge: t("ui.eegConfig.channelCatalog.groupBadge", {
          defaultValue: "Group",
        }),
      }));
  }, [channels, mode, search, systems, t]);

  const selectedChannels = useMemo(
    () =>
      channels
        .filter((channel) => mode === "electrodes" ? channel.system_id === selectedGroup : groupKeyForMode(channel, mode) === selectedGroup)
        .sort((a, b) => a.display_order - b.display_order),
    [channels, mode, selectedGroup],
  );

  const updateChannel = (
    channelId: string,
    patch: Partial<Channel>,
  ) => {
    setChannels((current) =>
      current.map((channel) =>
        channel.channel_id === channelId ? { ...channel, ...patch } : channel,
      ),
    );
  };

  const addChannel = () => {
    const draft = createDraftChannel(
      mode,
      selectedGroup || groups[0]?.id || copy.fallbackGroup,
    );
    const selectedSystem = systems.find((system) => system.system_id === selectedGroup);
    draft.system_id = selectedSystem?.system_id;
    draft.electrode_system = selectedSystem?.system_code || draft.electrode_system;
    setSearch("");
    setChannels((current) => [draft, ...current]);
    if (mode !== "electrodes") setSelectedGroup(groupKeyForMode(draft, mode));
    setRowSnapshots((current) => new Map(current).set(draft.channel_id, draft));
  };

  const beginRowEdit = (channel: Channel) => setRowSnapshots((current) => new Map(current).set(channel.channel_id, { ...channel }));
  const cancelRowEdit = (channel: Channel) => {
    const snapshot = rowSnapshots.get(channel.channel_id);
    setChannels((current) => /^(draft|import)-/.test(channel.channel_id) ? current.filter((row) => row.channel_id !== channel.channel_id) : current.map((row) => row.channel_id === channel.channel_id && snapshot ? snapshot : row));
    setRowSnapshots((current) => { const next = new Map(current); next.delete(channel.channel_id); return next; });
  };
  const saveRow = async (channel: Channel) => {
    try {
      const saved = /^(draft|import)-/.test(channel.channel_id) ? await createChannel(channel) : await saveChannel(channel.channel_id, channel);
      setChannels((current) => current.map((row) => row.channel_id === channel.channel_id ? saved : row));
      setRowSnapshots((current) => { const next = new Map(current); next.delete(channel.channel_id); return next; });
      toast.success("Electrode saved.");
    } catch (error) { toast.error(error instanceof Error ? error.message : "Failed to save electrode."); }
  };

  const addSystem = async () => {
    const name = window.prompt("Electrode system name"); if (!name) return;
    const code = window.prompt("System code", name.replace(/\s+/g, "-").toLowerCase()); if (!code) return;
    const created = await createElectrodeSystem({ system_name: name, system_code: code });
    setSystems((current) => [...current, created]); setSelectedGroup(created.system_id);
  };
  const cloneSystem = async () => {
    const selected = systems.find((system) => system.system_id === selectedGroup); if (!selected) return;
    const name = window.prompt("Name for cloned system", `${selected.system_name} Custom`); if (!name) return;
    const code = window.prompt("System code", `${selected.system_code}-custom`); if (!code) return;
    const created = await cloneElectrodeSystem(selected.system_id, { system_name: name, system_code: code });
    const rows = await fetchChannels(); setChannels(filterChannelsByMode(rows, mode)); setSystems(await fetchElectrodeSystems()); setSelectedGroup(created.system_id);
  };
  const archiveSystem = async () => {
    const selected = systems.find((system) => system.system_id === selectedGroup); if (!selected || !window.confirm(`Archive ${selected.system_name}?`)) return;
    await archiveElectrodeSystem(selected.system_id); const remaining = systems.filter((system) => system.system_id !== selected.system_id); setSystems(remaining); setSelectedGroup(remaining[0]?.system_id || "");
  };

  const importLayout = async (file: File) => {
    try {
      const imported = parseChannelsInterchange(await file.text(), file.name);
      const validation = validateChannelsInterchange(imported);
      if (!validation.valid) {
        toast.error(validation.errors[0] || "Invalid import file.");
        return;
      }
      if (validation.warnings.length > 0) {
        toast.warning(validation.warnings[0]);
      }

      let adapted = adaptImportedChannelsForMode(imported, mode);
      if (mode === "electrodes") {
        const selectedSystem = systems.find((system) => system.system_id === selectedGroup);
        if (!selectedSystem) { toast.error("Select an electrode system before importing."); return; }
        if (selectedSystem.is_standard && !isPlatformAdmin) { toast.error("Clone the standard system before importing electrodes."); return; }
        adapted = adapted.map((channel) => ({ ...channel, system_id: selectedSystem.system_id, electrode_system: selectedSystem.system_code }));
      }
      const filtered = filterChannelsByMode(adapted, mode);
      if (filtered.length === 0) {
        toast.info(
          t("ui.eegConfig.channelCatalog.importEmpty", {
            defaultValue: "No matching rows found for this catalog.",
          }),
        );
        return;
      }
      setChannels((current) => [...filtered, ...current]);
      if (mode !== "electrodes") setSelectedGroup(groupKeyForMode(filtered[0], mode));
      toast.success(
        t("ui.eegConfig.channelCatalog.imported", {
          count: filtered.length,
          format: detectInterchangeFormat(file.name).toUpperCase(),
          defaultValue: "Imported {{count}} {{format}} row(s). Save to persist.",
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("ui.eegConfig.channelCatalog.importFailed", {
              defaultValue: "Failed to import electrode layout.",
            }),
      );
    } finally {
      if (importInputRef.current) {
        importInputRef.current.value = "";
      }
    }
  };

  const openImportPicker = () => {
    const input = importInputRef.current;
    if (!input) return;
    input.value = "";
    input.click();
  };

  const exportLayout = () => {
    const rows = selectedChannels.length > 0 ? selectedChannels : channels;
    const extension = exportFormat === "tsv" ? "tsv" : exportFormat;
    const mimeType =
      exportFormat === "json"
        ? "application/json"
        : exportFormat === "xml"
          ? "application/xml"
          : "text/plain";
    const safeGroup = (selectedGroup || mode)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    downloadInterchangeFile(
      `eeg-${mode}-${safeGroup || "layout"}.${extension}`,
      serializeChannels(rows, exportFormat),
      mimeType,
    );
    toast.success(
      t("ui.eegConfig.channelCatalog.exported", {
        format: exportFormat.toUpperCase(),
        defaultValue: "Exported {{format}} layout.",
      }),
    );
  };

  const saveCatalog = async () => {
    try {
      const savedRows = await Promise.all(
        channels.map((channel) => {
          const payload = {
            channel_code: channel.channel_code,
            channel_name: channel.channel_name,
            electrode_system: channel.electrode_system,
            hemisphere: channel.hemisphere,
            region: channel.region,
            position_x: channel.position_x,
            position_y: channel.position_y,
            position_z: channel.position_z,
            digitized_x: channel.digitized_x,
            digitized_y: channel.digitized_y,
            digitized_z: channel.digitized_z,
            coordinate_source: channel.coordinate_source,
            coordinate_space: channel.coordinate_space,
            coordinate_units: channel.coordinate_units,
            display_order: channel.display_order,
            default_color: channel.default_color,
            abbreviation: channel.abbreviation,
            is_standard: channel.is_standard,
            age_group: channel.age_group,
            is_active: channel.is_active,
          };
          return channel.channel_id.startsWith("draft-")
            ? createChannel(payload)
            : saveChannel(channel.channel_id, payload);
        }),
      );
      const filtered = filterChannelsByMode(savedRows, mode);
      setChannels(filtered);
      setSelectedGroup((current) =>
        current || (filtered[0] ? groupKeyForMode(filtered[0], mode) : ""),
      );
      toast.success(
        t("ui.eegConfig.channelCatalog.saved", {
          title: localizedCopy.title,
          defaultValue: "{{title}} saved.",
        }),
      );
    } catch {
      toast.error(
        t("ui.eegConfig.channelCatalog.saveFailed", {
          title: localizedCopy.title.toLowerCase(),
          defaultValue: "Failed to save {{title}}.",
        }),
      );
    }
  };

  return (
    <EegConfigPageShell>
      <SettingsWorkbenchLayout
        title={localizedCopy.title}
        description={localizedCopy.description}
        listTitle={localizedCopy.listTitle}
        items={groups}
        selectedItemId={selectedGroup}
        searchValue={search}
        searchPlaceholder={t("ui.eegConfig.channelCatalog.searchPlaceholder", {
          listTitle: localizedCopy.listTitle.toLowerCase(),
          defaultValue: "Search {{listTitle}}",
        })}
        loading={loading}
        onSearchChange={setSearch}
        onSelectItem={(item) => setSelectedGroup(item.id)}
        footerActions={
          canManage ? <div className="d-grid gap-2">
            {mode === "electrodes" && <Button type="button" size="sm" variant="outline-primary" onClick={addSystem}><FaPlus className="me-1" />New system</Button>}
            <Button type="button" size="sm" variant="outline-primary" onClick={addChannel} disabled={mode === "electrodes" && systems.find((system) => system.system_id === selectedGroup)?.is_standard && !isPlatformAdmin}>
              <FaPlus className="me-1" />{localizedCopy.addLabel}
            </Button>
          </div> : null
        }
        actions={
          <div className="app-ui-stack-actions">
            {mode === "electrodes" && canManage && <>
              <Button size="sm" variant="outline-primary" onClick={cloneSystem}><FaClone className="me-1" />Clone</Button>
              <Button size="sm" variant="outline-danger" onClick={archiveSystem} disabled={systems.find((system) => system.system_id === selectedGroup)?.is_standard && !isPlatformAdmin}><FaArchive className="me-1" />Archive</Button>
            </>}
            <Form.Select
              size="sm"
              value={exportFormat}
              onChange={(event) =>
                setExportFormat(event.target.value as InterchangeFormat)
              }
              aria-label="Electrode layout export format"
              className="w-auto"
              {...passwordManagerIgnore}
            >
              <option value="tsv">Digitized electrode TSV</option>
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
              <option value="xml">XML</option>
            </Form.Select>
            <ButtonGroup size="sm">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={exportLayout}
                {...passwordManagerIgnore}
              >
                <FaDownload className="me-1" />
                Export
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={openImportPicker}
                {...passwordManagerIgnore}
              >
                <FaUpload className="me-1" />
                Import
              </Button>
            </ButtonGroup>
            <input
              ref={importInputRef}
              type="file"
              accept=".json,.csv,.tsv,.xml,.sfp,.ced,.locs,.loc,.elp"
              className="eeg-config-file-input"
              aria-label="Import electrode layout"
              tabIndex={-1}
              {...passwordManagerIgnore}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importLayout(file);
              }}
            />
            {mode !== "electrodes" && <Button type="button" variant="primary" onClick={saveCatalog}>
              <FaSave className="me-1" />
              <Trans ns="features" i18nKey="ui.copy.567e12738ddb" /></Button>}
          </div>
        }
      >
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <strong>
                {selectedGroup
                  ? mode === "electrodes" ? systems.find((system) => system.system_id === selectedGroup)?.system_name : formatGroupLabel(selectedGroup)
                  : "Select a group"}
              </strong>
              <div className="text-muted small">
                {selectedChannels.length} <Trans ns="features" i18nKey="ui.copy.4dfcc2c0aae6" />{selectedChannels.length === 1 ? "" : "s"}
              </div>
              {mode === "electrodes" && (
                <div className="text-muted small">
                  Coordinates use the normalized scalp layout (0–1). System values are locked until explicitly customized.
                </div>
              )}
            </div>
            <Badge bg="secondary">{mode}</Badge>
          </Card.Header>
          <Card.Body className="p-0">
            <div
              className="table-responsive eeg-config-table-wrap"
              tabIndex={0}
              role="region"
              aria-label="Electrode catalog table"
            >
              <Table hover className="mb-0 eeg-config-table">
                <colgroup>
                  <col className="eeg-config-channel-col-code" />
                  <col className="eeg-config-channel-col-name" />
                  <col className="eeg-config-channel-col-system" />
                  <col className="eeg-config-channel-col-region" />
                  <col className="eeg-config-channel-col-hemisphere" />
                  <col className="eeg-config-col-position" />
                  <col className="eeg-config-col-color--compact" />
                  <col className="eeg-config-channel-col-order" />
                  <col className="eeg-config-channel-col-active" />
                  <col className="eeg-config-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th><Trans ns="features" i18nKey="ui.copy.adac69379a62" /></th>
                    <th><Trans ns="features" i18nKey="ui.copy.709a23220f2c" /></th>
                    <th><Trans ns="features" i18nKey="ui.copy.bc0792d8dc81" /></th>
                    <th><Trans ns="features" i18nKey="ui.copy.0f217179940c" /></th>
                    <th><Trans ns="features" i18nKey="ui.copy.351d5ab58639" /></th>
                    <th className="eeg-config-table__cell--position">
                      <Trans ns="features" i18nKey="ui.copy.cf1c85adba54" />
                    </th>
                    <th className="eeg-config-table__cell--color eeg-config-table__cell--color--compact">
                      <Trans ns="features" i18nKey="ui.copy.1d0c8304baed" />
                    </th>
                    <th title="Controls the order of electrodes within this system">Display order</th>
                    <th><Trans ns="features" i18nKey="ui.copy.a733b809d2f1" /></th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedChannels.map((channel) => {
                    const source = channel.coordinate_source || "system_default";
                    const isDraft = channel.channel_id.startsWith("draft-") || channel.channel_id.startsWith("import-");
                    const rowIsEditing = isDraft || rowSnapshots.has(channel.channel_id);
                    const isEditing = rowIsEditing && (isDraft || editingPositions.has(channel.channel_id));
                    const coordinateInvalid = [channel.position_x, channel.position_y].some(
                      (value) => value != null && (value < 0 || value > 1),
                    );
                    return (
                    <tr key={channel.channel_id}>
                      <td>
                        <Form.Control
                          disabled={!rowIsEditing}
                          value={channel.channel_code}
                          aria-label={`${channel.channel_code || "Channel"} code`}
                          onChange={(event) =>
                            updateChannel(channel.channel_id, {
                              channel_code: event.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          disabled={!rowIsEditing}
                          value={channel.channel_name}
                          aria-label={`${channel.channel_code || "Channel"} name`}
                          onChange={(event) =>
                            updateChannel(channel.channel_id, {
                              channel_name: event.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          disabled={!rowIsEditing}
                          value={channel.electrode_system}
                          aria-label={`${channel.channel_code || "Channel"} electrode system`}
                          onChange={(event) =>
                            updateChannel(channel.channel_id, {
                              electrode_system: event.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          disabled={!rowIsEditing}
                          value={channel.region}
                          aria-label={`${channel.channel_code || "Channel"} region`}
                          onChange={(event) =>
                            updateChannel(channel.channel_id, {
                              region: event.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <Form.Select
                          disabled={!rowIsEditing}
                          value={channel.hemisphere || "midline"}
                          aria-label={`${channel.channel_code || "Channel"} hemisphere`}
                          onChange={(event) =>
                            updateChannel(channel.channel_id, {
                              hemisphere: event.target.value,
                            })
                          }
                        >
                          <option value="left"><Trans ns="features" i18nKey="ui.copy.8ae1c34bd37f" /></option>
                          <option value="right"><Trans ns="features" i18nKey="ui.copy.954daa8b0033" /></option>
                          <option value="midline"><Trans ns="features" i18nKey="ui.copy.2861951840e7" /></option>
                          <option value="bilateral"><Trans ns="features" i18nKey="ui.copy.8e3171956ab7" /></option>
                        </Form.Select>
                      </td>
                      <td className="eeg-config-table__cell--position">
                        <div className="eeg-config-position-editor" aria-label="Electrode position coordinates">
                          <div className="eeg-config-coordinate-meta">
                            <span className="eeg-config-position-preview" aria-hidden="true">
                              <span style={{ left: `${(channel.position_x ?? 0.5) * 100}%`, top: `${(1 - (channel.position_y ?? 0.5)) * 100}%` }} />
                            </span>
                            {source !== "system_default" && (
                              <Badge bg={source === "custom_normalized" ? "warning" : "info"} text={source === "custom_normalized" ? "dark" : undefined}>
                                {source === "custom_normalized" ? "Custom" : "Imported"}
                              </Badge>
                            )}
                          </div>
                          {isEditing ? <div className="eeg-config-position-fields">
                          <label className="eeg-config-mini-field">
                            <span className="eeg-config-mini-field__label">
                              X
                            </span>
                          <Form.Control
                            disabled={!rowIsEditing}
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={channel.position_x ?? 0}
                            aria-label={`${channel.channel_code || "Channel"} position X`}
                            onChange={(event) =>
                              updateChannel(channel.channel_id, {
                                position_x: Number(event.target.value),
                              })
                            }
                          />
                          </label>
                          <label className="eeg-config-mini-field">
                            <span className="eeg-config-mini-field__label">
                              Y
                            </span>
                          <Form.Control
                            disabled={!rowIsEditing}
                            type="number"
                            min="0"
                            max="1"
                            step="0.01"
                            value={channel.position_y ?? 0}
                            aria-label={`${channel.channel_code || "Channel"} position Y`}
                            onChange={(event) =>
                              updateChannel(channel.channel_id, {
                                position_y: Number(event.target.value),
                              })
                            }
                          />
                          </label>
                          </div> : <div className="eeg-config-coordinate-values">
                            <span><strong>X</strong> {(channel.position_x ?? 0).toFixed(2)}</span>
                            <span><strong>Y</strong> {(channel.position_y ?? 0).toFixed(2)}</span>
                          </div>}
                          {coordinateInvalid && <div className="text-danger small">Enter values from 0 to 1.</div>}
                          <div className="eeg-config-coordinate-actions">
                            {rowIsEditing && !isEditing && <Button size="sm" variant="link" aria-label={`Edit ${channel.channel_code} coordinates`} title="Edit coordinates" onClick={() => {
                              setEditingPositions((current) => new Set(current).add(channel.channel_id));
                              updateChannel(channel.channel_id, { coordinate_source: "custom_normalized" });
                            }}><FaEdit aria-hidden="true" /></Button>}
                            {source === "custom_normalized" && channel.default_position_x != null && channel.default_position_y != null && (
                              <Button size="sm" variant="link" onClick={() => {
                                updateChannel(channel.channel_id, {
                                  position_x: channel.default_position_x,
                                  position_y: channel.default_position_y,
                                  position_z: channel.default_position_z,
                                  coordinate_source: "system_default",
                                });
                                setEditingPositions((current) => { const next = new Set(current); next.delete(channel.channel_id); return next; });
                              }}><FaUndo /> Reset</Button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="eeg-config-table__cell--color eeg-config-table__cell--color--compact">
                        <div className="eeg-config-color-field">
                          <Form.Control
                            disabled={!rowIsEditing}
                            type="color"
                            value={channel.default_color || "#0d6efd"}
                            aria-label={`${channel.channel_code || "Channel"} color`}
                            onChange={(event) =>
                              updateChannel(channel.channel_id, {
                                default_color: event.target.value,
                              })
                            }
                          />
                        </div>
                      </td>
                      <td>
                        {rowIsEditing ? <Form.Control
                          type="number" min="0" step="1"
                          value={channel.display_order}
                          aria-label={`${channel.channel_code || "Channel"} display order`}
                          onChange={(event) => updateChannel(channel.channel_id, { display_order: Number(event.target.value) })}
                        /> : <span className="eeg-config-order-value">{channel.display_order}</span>}
                      </td>
                      <td>
                        <Form.Check
                          disabled={!rowIsEditing}
                          type="switch"
                          checked={channel.is_active}
                          onChange={(event) =>
                            updateChannel(channel.channel_id, {
                              is_active: event.target.checked,
                            })
                          }
                          aria-label={`Toggle ${channel.channel_code}`}
                        />
                      </td>
                      <td>
                        {rowIsEditing ? <ButtonGroup size="sm">
                          <Button variant="outline-primary" aria-label={`Save ${channel.channel_code}`} onClick={() => void saveRow(channel)}><FaSave /></Button>
                          <Button variant="outline-secondary" aria-label={`Cancel ${channel.channel_code}`} onClick={() => cancelRowEdit(channel)}><FaTimes /></Button>
                        </ButtonGroup> : canManage && <Button size="sm" variant="link" aria-label={`Edit ${channel.channel_code}`} disabled={systems.find((system) => system.system_id === channel.system_id)?.is_standard && !isPlatformAdmin} onClick={() => beginRowEdit(channel)}><FaEdit /></Button>}
                      </td>
                    </tr>
                    );
                  })}
                  {selectedChannels.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center text-muted p-4">
                        <Trans ns="features" i18nKey="ui.copy.cd228892ba09" /></td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </SettingsWorkbenchLayout>
    </EegConfigPageShell>
  );
};

export default ChannelCatalogPage;
